import Vue from 'vue';
import Vuex from 'vuex';
import createPersistedState from "vuex-persistedstate";
import {DvdCartItem, DvdOrderContact, DvdOrderDetails, SportEventDvd} from '@/dvd-orders/DvdModel';
import BigNumber from 'bignumber.js';
import {getStates} from '@/dvd-orders/DvdOrderService';
import {localStorageEnabled} from '@/utils/localstorageUtil';

interface State {
    dvdOrderDetails: DvdOrderDetails;
    confirmationNumber: string;
    states: State[];
    dvdOrderRecentSearches: string[];
}

Vue.use(Vuex);

const plugins = [];
if (localStorageEnabled()) {
    plugins.push(createPersistedState({ paths: ['dvdOrderDetails', 'confirmationNumber', 'dvdOrderRecentSearches'] }));
}

export default new Vuex.Store({
    state: {
        dvdOrderDetails: defaultDetails(),
        confirmationNumber: '',
        states: [],
        dvdOrderRecentSearches: []
    },
    mutations: {
        addToCart(state: State, item: SportEventDvd) {
            state.dvdOrderDetails.items.push({
                dvdId: item.dvdId,
                eventId: item.eventId,
                quantity: 1,
                title: item.title,
                description: item.description,
                subcategory: item.subcategory,
                date: item.formattedDate!,
                price: item.price
            });
            state.dvdOrderDetails.taxAmount = "0";
            calculateAmounts(state.dvdOrderDetails);
        },
        removeFromCart(state: State, item: SportEventDvd) {
            const index = state.dvdOrderDetails.items.findIndex(includesPredicate(item));
            if (index > -1) {
                state.dvdOrderDetails.items.splice(index, 1);
            }
            state.dvdOrderDetails.taxAmount = "0";
            calculateAmounts(state.dvdOrderDetails);
        },
        clearCart(state: State) {
            state.dvdOrderDetails = defaultDetails();
        },
        updateQuantity(state: State, item: DvdCartItem) {
            const index = state.dvdOrderDetails.items.findIndex(includesPredicate(item));
            state.dvdOrderDetails.items[index].quantity = item.quantity;
            state.dvdOrderDetails.taxAmount = "0";
            calculateAmounts(state.dvdOrderDetails);
        },
        updateBilling(state: State, info: DvdOrderContact) {
            state.dvdOrderDetails.billingInfo = info;
        },
        updateShipping(state: State, info: DvdOrderContact) {
            const existing = state.dvdOrderDetails.shippingInfo;
            state.dvdOrderDetails.shippingInfo = info;
            if (!existing
                || !existing.zip || existing.zip !== info.zip
                || !existing.city || existing.city !== info.city
                || !existing.stateId || existing.stateId !== info.stateId
                || !existing.address || existing.address !== info.address ) {
                state.dvdOrderDetails.taxAmount = "0";
                calculateAmounts(state.dvdOrderDetails);
            }
        },
        updateIsInfoTheSame(state: State, isInfoTheSame: boolean) {
            state.dvdOrderDetails.isInfoTheSame = isInfoTheSame;
        },
        orderSubmitted(state: State, confirmationNumber: string) {
            state.confirmationNumber = confirmationNumber;
        },
        updateTaxes(state: State, taxAmount: string) {
            state.dvdOrderDetails.taxAmount = taxAmount;
            calculateAmounts(state.dvdOrderDetails);
        },
        setStates(state: State, states: State[]) {
            state.states = states;
        },
        addSearch(state: State, text: string) {
            if (text) {
                // remove if exists to keep the list fresh with recent searches
                const dvdOrderRecentSearches = state.dvdOrderRecentSearches
                .filter(item => !text.toLowerCase().startsWith(item.toLowerCase()));
                state.dvdOrderRecentSearches = [text, ...dvdOrderRecentSearches];
            }
        },
        removeSearch(state: State, text: string) {
            if (text) {
                const textIndex = state.dvdOrderRecentSearches.indexOf(text);
                if (textIndex >= 0) {
                    state.dvdOrderRecentSearches.splice(textIndex, 1);
                }
            }
        },
        clearSearches(state: State) {
            state.dvdOrderRecentSearches = [];
        }
    },
    getters: {
        isInCart(state: State) {
            return (item: SportEventDvd) => {
                return Boolean(state.dvdOrderDetails.items.find(includesPredicate(item)));
            }
        },
        contactInfoComplete(state: State) {
            const details = state.dvdOrderDetails;
            return checkField(details.shippingInfo.firstName) &&
                checkField(details.shippingInfo.lastName) &&
                checkField(details.shippingInfo.email) &&
                checkField(details.shippingInfo.address) &&
                checkField(details.shippingInfo.city) &&
                checkField(details.shippingInfo.stateId) &&
                checkField(details.shippingInfo.zip) &&
                checkField(details.billingInfo.firstName) &&
                checkField(details.billingInfo.lastName) &&
                checkField(details.billingInfo.email) &&
                checkField(details.billingInfo.address) &&
                checkField(details.billingInfo.city) &&
                checkField(details.billingInfo.stateId) &&
                checkField(details.billingInfo.zip);
        },
        allGreaterThan0(state: State) {
            return state.dvdOrderDetails.items.every(item => item.quantity > 0);
        },
        recentSearches(state: State) {
            // get top 5
            const searches = state.dvdOrderRecentSearches;
            if (searches.length) {
                return searches.slice(0,5);
            }
            return [];
        }
    },
    actions: {
        async loadStates({ commit }) {
            commit('setStates', await getStates());
        }
    },
    plugins: plugins
});

function includesPredicate(item: { eventId?: number, dvdId?: number }) {
    return (itemToCheck: DvdCartItem) => item.eventId === itemToCheck.eventId && item.dvdId === itemToCheck.dvdId;
}

function calculateAmounts(details: DvdOrderDetails) {
    details.totalQuantity = details.items.reduce((current: number, item: DvdCartItem) => current + item.quantity, 0);
    details.subtotalAmount = details.items.map((item: DvdCartItem) => new BigNumber(item.quantity).multipliedBy(item.price))
        .reduce((curr, prev) => curr.plus(prev), new BigNumber(0))
        .toFixed(2, BigNumber.ROUND_HALF_EVEN);
    details.taxAmount = new BigNumber(details.taxAmount || 0).toFixed(2, BigNumber.ROUND_HALF_EVEN);
    details.totalAmount = new BigNumber(details.subtotalAmount).plus(details.taxAmount)
        .toFixed(2, BigNumber.ROUND_HALF_EVEN);
}

function defaultDetails(): DvdOrderDetails {
    return {
        billingInfo: {
            firstName: '',
                lastName: '',
                email: '',
                address: '',
                city: '',
                stateId: 0,
                zip: '',
        },
        isInfoTheSame: false,
        shippingInfo: {
            firstName: '',
                lastName: '',
                email: '',
                address: '',
                city: '',
                stateId: 0,
                zip: '',
        },
        items: [],
            totalQuantity: 0,
            subtotalAmount: '',
            taxAmount: '',
            totalAmount: ''
    };
}

function checkField(field: any): boolean {
    return Boolean(field && String(field).trim());
}
